import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import extractText from 'flarum/common/utils/extractText';
import AutoReloadingPage from './AutoReloadingPage';
import ExhibitionEditModal from './ExhibitionEditModal';
import ExhibitionFindingEditModal from './ExhibitionFindingEditModal';
import ExhibitionQrCodeModal from './ExhibitionQrCodeModal';
import ExportLinkButton from './ExportLinkButton';
import ListEnd from './ListEnd';
import RecentFindingsModal from './RecentFindingsModal';
import confidenceOptions from '../utils/confidenceOptions';
import FindingsSlideshow from '../utils/FindingsSlideshow';
import formatDate from '../utils/formatDate';
import PagedList from '../utils/PagedList';
import uploadFile from '../utils/uploadFile';
import { loginRequiredPlaceholder } from '../utils/requireLogin';
// Rows a page, as the list scrolls; the API's maximum page.
const PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 500;

/**
 * /exhibitions/:id - one exhibition's metadata plus its findings checklist.
 * The société shown after the exhibition's own name in the title is a
 * second, separate request against champis-exhibitions-societies matched
 * by groupId client-side (mirrors ExhibitionsListPage's own
 * exhibitionsFor()) - not a relationship on Exhibition itself, since
 * GroupResource can't resolve the hidden SOC_MYCO_* groups for a plain
 * member either (see ExhibitionResource::fields()'s groupId docblock for
 * why that field is a plain integer, not a relationship) - SocietyResource's
 * own scope() already limits this to sociétés the actor belongs to, which
 * always includes this exhibition's own (ExhibitionPolicy requires the
 * same membership just to view the exhibition at all).
 *
 * The findings come a page at a time as the list scrolls (PagedList,
 * ListEnd), searched and sorted by number then name by the server
 * (ExhibitionFindingResource) - by phylum then name for a société sorting
 * its cards so - as the exports are. After an addition, an
 * edit or a deletion, the rows shown are loaded again where they are.
 */
export default class ExhibitionPage extends AutoReloadingPage {
  exhibition = null;
  society = null;
  findingsSearch = '';
  findings = (() => new PagedList('champis-exhibitions-findings', () => ({
    filter: {
      exhibition: this.exhibition.id(),
      ...(this.findingsSearch.trim() ? {
        q: this.findingsSearch.trim()
      } : {})
    },
    // In its société's cards' order - see Exhibition::sortsByPhylum().
    sort: this.society?.cartothequeSortByPhylum() ? 'phylum' : 'number'
  }), {
    pageSize: PAGE_SIZE,
    maxPageSize: MAX_PAGE_SIZE
  }))();
  slideshow = (() => new FindingsSlideshow(this.findings, finding => this.findingName(finding)))();
  uploadingFindingId = null;
  resetOnCreate() {
    this.findingsSearch = '';
    // Which finding's own quick-add-photo upload (see findingRow()) is
    // currently in flight, if any - a plain id rather than a set, since
    // there's normally only ever one file input being used at a time.
    this.uploadingFindingId = null;
  }
  resetData() {
    this.exhibition = null;
    this.society = null;
    this.findings.clear();
  }
  currentId() {
    return this.exhibition && this.exhibition.id();
  }

  // The société fetch below is ->authenticated() (SocietyResource) - a
  // guest's doomed request is already skipped one level up, in
  // AutoReloadingPage's own load() (see requireLogin.js's own docblock).
  fetch() {
    return Promise.all([app.store.find('champis-exhibitions', m.route.param('id')), app.store.find('champis-exhibitions-societies')]);
  }
  onLoaded(_ref) {
    let [exhibition, societies] = _ref;
    this.exhibition = exhibition;
    this.society = societies.find(society => String(society.groupId()) === String(exhibition.groupId())) || null;
    app.setTitle(exhibition.name());
    m.redraw();
    this.findings.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.findings.clear();
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`, params);
    };
    if (this.requiresLogin) {
      return m("div", {
        className: "Page ChampisExhibitionPage"
      }, m("div", {
        className: "container"
      }, loginRequiredPlaceholder(t('login_required'))));
    }
    if (this.error) {
      return m("div", {
        className: "Page ChampisExhibitionPage"
      }, m("div", {
        className: "container"
      }, m(Placeholder, {
        text: t('error')
      })));
    }
    if (!this.exhibition) {
      return m("div", {
        className: "Page ChampisExhibitionPage"
      }, m("div", {
        className: "container"
      }, m(LoadingIndicator, null)));
    }
    const exhibition = this.exhibition;
    const apiUrl = app.forum.attribute('apiUrl');
    const findings = this.findings;
    // What the rows shown were searched for.
    const searched = findings.currentParams.filter?.q;
    return m("div", {
      className: "Page ChampisExhibitionPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "ChampisDiscussionList-header"
    }, m(Link, {
      href: app.route('exhibitions'),
      className: "ChampisExhibitions-backLink ChampisExhibitions-backLink--prominent"
    }, m("i", {
      className: "icon fas fa-chevron-left",
      "aria-hidden": "true"
    }), " ", t('back_to_exhibitions')), m("div", {
      className: "ChampisExhibitions-headerRow"
    }, m("div", null, m("i", {
      className: "icon fas fa-campground",
      "aria-hidden": "true"
    }), " ", exhibition.name()), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, m(Button, {
      icon: "fas fa-pencil-alt",
      className: "Button",
      onclick: () => this.openEditModal()
    }, t('edit_button')), m(Button, {
      icon: "fas fa-trash",
      className: "Button",
      onclick: () => this.delete()
    }, t('delete_button'))))), m("div", {
      className: "ChampisExhibitionPage-meta"
    }, this.society && m("div", null, this.society.name()), m("div", null, this.dateRange(exhibition)), exhibition.location() && m("div", null, exhibition.location()), exhibition.description() && m("div", {
      className: "ChampisExhibitionPage-description"
    }, exhibition.description()), m("div", {
      className: "ChampisExhibitionPage-metaActions"
    }, m(ExportLinkButton, {
      icon: "fa-file-excel",
      label: t('export_xlsx_button'),
      href: `${apiUrl}/champis-exhibitions/${exhibition.id()}/findings.xlsx`
    }), m(ExportLinkButton, {
      icon: "fa-file-pdf",
      label: t('export_pdf_button'),
      href: `${apiUrl}/champis-exhibitions/${exhibition.id()}/findings.pdf`
    }), m(Link, {
      href: app.route('expo', {
        id: exhibition.id()
      }),
      className: "Button ChampisExhibitions-exportButton"
    }, m("i", {
      className: "icon fas fa-globe Button-icon",
      "aria-hidden": "true"
    }), m("span", {
      className: "Button-label ChampisExhibitions-exportButtonLabel"
    }, t('public_page_button'))), m(Button, {
      className: "Button ChampisExhibitions-exportButton",
      icon: "fas fa-qrcode",
      onclick: () => app.modal.show(ExhibitionQrCodeModal, {
        exhibition
      })
    }, m("span", {
      className: "ChampisExhibitions-exportButtonLabel"
    }, t('qr_code_button'))), m(Button, {
      className: "Button ChampisExhibitions-exportButton",
      icon: "fas fa-history",
      onclick: () => this.openRecentFindingsModal()
    }, m("span", {
      className: "ChampisExhibitions-exportButtonLabel"
    }, t('recent_findings_button')))), m("div", {
      className: "ChampisExhibitionPage-embedCodeHint"
    }, t('embed_code_hint', {
      code: m("span", {
        className: "ChampisExhibitionPage-embedCode"
      }, m("code", null, this.embedCode(exhibition)), m("a", {
        "aria-label": extractText(t('embed_code_copy_button')),
        title: extractText(t('embed_code_copy_button')),
        onclick: () => this.copyEmbedCode(exhibition)
      }, m("i", {
        className: "icon fas fa-copy",
        "aria-hidden": "true"
      }))),
      plugin: m("a", {
        href: "https://fr.wordpress.org/plugins/champis-net/",
        target: "_blank",
        rel: "noopener noreferrer"
      }, "champis-net")
    }))), m("div", {
      className: "ChampisExhibitionPage-findingsHeader"
    }, m("h3", null, t('findings_title', {
      count: findings.total ?? exhibition.findingsCount() ?? 0
    })), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, this.society &&
    // ?exhibition=<id> - CartothequePage's own back link reads
    // this to return here instead of to /societies/:id (see
    // that page's own docblock).
    m(Link, {
      href: `${app.route('cartotheque', {
        id: this.society.id()
      })}?exhibition=${exhibition.id()}`,
      className: "Button"
    }, m("i", {
      className: "icon fas fa-address-card",
      "aria-hidden": "true"
    }), " ", t('cartotheque_button')), m(Button, {
      icon: "fas fa-plus",
      className: "Button Button--primary",
      onclick: () => this.openAddFindingModal()
    }, t('add_finding_button')))), m("div", {
      className: "ChampisExhibitionPage-findingsBody"
    }, (searched || (findings.total ?? exhibition.findingsCount() ?? 0) > 0) && m("input", {
      className: "FormControl ChampisExhibitionPage-search",
      type: "search",
      placeholder: extractText(t('search_placeholder')),
      "aria-label": extractText(t('search_placeholder')),
      value: this.findingsSearch,
      oninput: e => {
        this.findingsSearch = e.target.value;
        findings.reloadSoon();
      }
    }), findings.items.length > 0 && m("ul", {
      className: "ChampisExhibitionPage-findings"
    }, findings.items.map(finding => this.findingRow(finding))), findings.error ? m("div", {
      className: "ChampisExpoPage-error"
    }, m(Placeholder, {
      text: t('findings_error')
    }), m(Button, {
      className: "Button",
      onclick: () => findings.items.length > 0 ? findings.refresh() : findings.load()
    }, t('retry_button'))) : !findings.loading && findings.items.length === 0 && m(Placeholder, {
      text: t(searched ? 'search_no_results' : 'findings_empty')
    })), m(ListEnd, {
      list: findings
    })));
  }
  findingRow(finding) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`, params);
    };
    const display = finding.speciesDisplay();
    const confidenceLabel = this.confidenceLabelFor(finding.cfId());
    const photos = finding.photos() || [];
    return m("li", {
      key: finding.id(),
      className: "ChampisExhibitionPage-finding"
    }, m("div", {
      className: "ChampisExhibitionPage-findingBody"
    }, m("div", {
      className: "ChampisExhibitionPage-findingName"
    }, finding.label() && m("span", {
      className: "ChampisExhibitionPage-findingNo"
    }, finding.label()), finding.gbifId() === 0 ? t('undetermined') : display ? m("span", null, m("em", null, display.canonicalName || display.scientificName), " ", display.author) : t('unknown_species')), m("div", {
      className: "ChampisExhibitionPage-findingMeta"
    }, m("span", {
      className: "ChampisExhibitionPage-findingConfidence"
    }, confidenceLabel), finding.vernacularName() && m("span", null, finding.vernacularName())), finding.notes() && m("div", {
      className: "ChampisExhibitionPage-findingNotes"
    }, finding.notes())), photos.length > 0 && m("div", {
      className: "ChampisExhibitionPage-findingPhotos"
    }, photos.map(photo =>
    // The public page's: its thumbnail, loaded once in sight,
    // and every photo of the list in the lightbox from it.
    m("a", {
      key: photo.url,
      href: photo.url,
      target: "_blank",
      rel: "noopener noreferrer",
      "aria-label": extractText(t('photo_label', {
        name: this.findingName(finding)
      })),
      onclick: e => this.slideshow.open(e, finding, photo)
    }, m("img", {
      className: "ChampisExhibitionPage-findingPhoto",
      src: photo.thumbnailUrl || photo.url,
      alt: "",
      loading: "lazy"
    })))), m("div", {
      className: "ChampisExhibitionPage-findingActions"
    }, photos.length < 5 && m("a", {
      "aria-label": extractText(t('add_photo_button')),
      title: extractText(t('add_photo_button')),
      onclick: () => document.getElementById(`ChampisExhibitionPage-photoInput-${finding.id()}`)?.click()
    }, this.uploadingFindingId === finding.id() ? m("i", {
      className: "icon fas fa-spinner fa-spin",
      "aria-hidden": "true"
    }) : m("i", {
      className: "icon fas fa-camera",
      "aria-hidden": "true"
    })), m("input", {
      id: `ChampisExhibitionPage-photoInput-${finding.id()}`,
      className: "ChampisExhibitionPage-hiddenPhotoInput",
      type: "file",
      accept: "image/*",
      "aria-hidden": "true",
      tabindex: "-1",
      onchange: e => this.uploadFindingPhoto(finding, e.target.files?.[0])
    }), m("a", {
      "aria-label": extractText(t('edit_finding_button')),
      title: extractText(t('edit_finding_button')),
      onclick: () => this.openEditFindingModal(finding)
    }, m("i", {
      className: "icon fas fa-pencil-alt",
      "aria-hidden": "true"
    })), m("a", {
      "aria-label": extractText(t('delete_finding_button')),
      title: extractText(t('delete_finding_button')),
      onclick: () => this.deleteFinding(finding)
    }, m("i", {
      className: "icon fas fa-trash",
      "aria-hidden": "true"
    }))));
  }

  // Its species as the list names it, in words: for the photos' captions.
  findingName(finding) {
    const t = key => extractText(app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`));
    const display = finding.speciesDisplay();
    if (finding.gbifId() === 0) return t('undetermined');
    return display ? display.canonicalName || display.scientificName || t('unknown_species') : t('unknown_species');
  }

  // [champis-expo-{id}] - the champis-net WordPress plugin's own pseudo-
  // shortcode syntax (a plain regex match against post content, not a real
  // add_shortcode() - see champis-net.php's champis_net_add_expo_react()),
  // which it rewrites into a <div class="champis-expo" data-exhibition-id>
  // that its React bundle then hydrates by fetching this same exhibition
  // from GET /api/champis-exhibitions/{id}. Hand-typing this into a
  // WordPress post is error-prone (wrong prefix, missing bracket), hence
  // a copy button here instead.
  embedCode(exhibition) {
    return `[champis-expo-${exhibition.id()}]`;
  }
  copyEmbedCode(exhibition) {
    const t = key => app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`);
    navigator.clipboard.writeText(this.embedCode(exhibition)).then(() => app.alerts.show({
      type: 'success'
    }, t('embed_code_copy_success')), () => app.alerts.show({
      type: 'error'
    }, t('embed_code_copy_error')));
  }
  confidenceLabelFor(cfId) {
    const match = confidenceOptions().find(o => o.cfId === cfId);
    return match ? match.label : cfId;
  }
  dateRange(exhibition) {
    const start = exhibition.startDate();
    const end = exhibition.endDate();
    return end && end !== start ? `${formatDate(start)} → ${formatDate(end)}` : formatDate(start);
  }
  openEditModal() {
    app.modal.show(ExhibitionEditModal, {
      exhibition: this.exhibition,
      society: this.society,
      onsaved: () => m.redraw()
    });
  }
  openAddFindingModal() {
    app.modal.show(ExhibitionFindingEditModal, {
      exhibition: this.exhibition,
      onsaved: () => this.findings.refresh()
    });
  }
  openRecentFindingsModal() {
    app.modal.show(RecentFindingsModal, {
      exhibition: this.exhibition
    });
  }
  openEditFindingModal(finding) {
    app.modal.show(ExhibitionFindingEditModal, {
      exhibition: this.exhibition,
      finding,
      onsaved: () => m.redraw()
    });
  }
  delete() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`);
    if (!confirm(extractText(t('delete_confirm')))) return;
    this.exhibition.delete().then(() => {
      m.route.set(app.route('exhibitions'));
    });
  }
  deleteFinding(finding) {
    const t = key => app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`);
    if (!confirm(extractText(t('delete_finding_confirm')))) return;

    // Loaded again where they are: the row gone, the next one in.
    finding.delete().then(() => this.findings.refresh());
  }
  uploadFindingPhoto(finding, file) {
    if (!file) return;
    const t = key => app.translator.trans(`champis-exhibitions.forum.exhibition_page.${key}`);
    this.uploadingFindingId = finding.id();
    m.redraw();
    uploadFile(file).then(uploaded => {
      if (!uploaded) return Promise.resolve();

      // Model.save() only ever sends the attributes passed to it (not
      // the finding's other, unrelated fields), so label/vernacularName/
      // notes/etc. can't be accidentally clobbered with stale in-memory
      // values the way resending an entire form would risk. gbifId does
      // have to be resent alongside photos though - ExhibitionFindingResource
      // declares it ->required() unconditionally (confirmed directly: a
      // bare {photos: [...]} PATCH 422s on "gbif id est obligatoire"),
      // not just on create - the finding's own already-loaded value is
      // simply echoed back, never actually changing it.
      const photos = (finding.photos() || []).map(photo => ({
        url: photo.url,
        type: photo.type
      }));
      photos.push({
        url: uploaded.attributes.url,
        type: uploaded.attributes.type
      });
      return finding.save({
        gbifId: finding.gbifId(),
        photos
      });
    }).then(() => {
      this.uploadingFindingId = null;
      m.redraw();
    }).catch(() => {
      this.uploadingFindingId = null;
      app.alerts.show({
        type: 'error'
      }, t('add_photo_error'));
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExhibitionPage', ExhibitionPage);